/**
 * E-Signature Legality Guide — public surface of the registry.
 *
 * ⚠️ Pages MUST read counts from here, never write a number in prose.
 * `countryCount()` is the only place a coverage figure comes from, so the
 * figure can always be recounted from the data behind it. That is the whole
 * defence against re-acquiring an unsourced claim like the "180+ countries"
 * removed in 96cab3a.
 */
import { LEGAL_FAMILIES } from "./families";
import { LEGALITY_COUNTRIES } from "./countries";
import type { LegalFamily, LegalityCountry, SourceTable } from "./types";

export type { LegalFamily, LegalityCountry, Instrument, SignatureTier, Depth, SourceTable } from "./types";
export type { SourceCitation } from "./sources";
export { LEGAL_FAMILIES } from "./families";
export { LEGALITY_COUNTRIES } from "./countries";
export { SOURCE_TABLE_CITATIONS, citationFor } from "./sources";

/* ── Families ── */

export function getFamilyBySlug(slug: string): LegalFamily | undefined {
  return LEGAL_FAMILIES.find((f) => f.slug === slug);
}

export function getFamilyById(id: string): LegalFamily | undefined {
  return LEGAL_FAMILIES.find((f) => f.id === id);
}

export function familySlugs(): string[] {
  return LEGAL_FAMILIES.map((f) => f.slug);
}

/* ── Countries ── */

export function getCountryByIso2(iso2: string): LegalityCountry | undefined {
  const key = iso2.toUpperCase();
  return LEGALITY_COUNTRIES.find((c) => c.iso2 === key);
}

export function countriesInFamily(familyId: string): LegalityCountry[] {
  return LEGALITY_COUNTRIES.filter((c) => c.familyId === familyId).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export function countriesBySource(source: SourceTable): LegalityCountry[] {
  return LEGALITY_COUNTRIES.filter((c) => c.sourceTable === source);
}

/**
 * 🔴 The coverage figure. The ONLY source of a country count anywhere in the
 * product. Never inline a literal alongside this — a number a reader cannot
 * recount from the page is how the last one went wrong.
 */
export function countryCount(): number {
  return LEGALITY_COUNTRIES.length;
}

/** Countries carrying a full page, sorted for display. */
export function fullDepthCountries(): LegalityCountry[] {
  return LEGALITY_COUNTRIES.filter((c) => c.depth === "full").sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/** All countries, alphabetical — the hub index. */
export function allCountriesSorted(): LegalityCountry[] {
  return [...LEGALITY_COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
}
